import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { CdkMenu } from '@angular/cdk/menu';
import {
  FilterMenuElement,
  FilterMenuElementItem,
} from '../filter-menu-element/filter-menu-element';
import { SortDirectionMenuElement } from '../sort-direction-menu-element/sort-direction-menu-element';
import { RangeMenuElement } from '../range-menu-element/range-menu-element';
import { FilterService } from '../../../services/filters.service';
import { TableColumnsService } from '../../../services/table-columns.service';
import { FilterMenuConfig, FilterType } from '../../../types/filter.type';
import { CheckboxMenuElement } from '../checkbox-menu-element/checkbox-menu-element';
import { SearchMenuElement } from '../search-menu-element/search-menu-element';
import { RadioMenuElement } from '../radio-menu-element/radio-menu-element';

@Component({
  selector: 'app-filter-menu',
  imports: [
    FilterMenuElement,
    SortDirectionMenuElement,
    RangeMenuElement,
    CheckboxMenuElement,
    SearchMenuElement,
    RadioMenuElement,
  ],
  hostDirectives: [CdkMenu],
  template: `
    @if (!config() || config().sortable !== false) {
      <app-sort-direction-menu-element [column]="column()" />
    }
    @if (filters(); as filters) {
      @if (filters.length !== 0) {
        <section class="filter-content">
          <div class="filter-content-header">
            <p>{{ 'Sélection : ' }}</p>
            @if (hasActiveMenuState()) {
              <button
                type="button"
                (click)="filterService.resetFilter(column()); $event.stopPropagation()"
              >
                Réinit.
              </button>
            }
          </div>
          @for (filter of filters; track $index; let filterIndex = $index) {
            @switch (filter.type) {
              @case ('checkbox') {
                @for (data of filter.data; track $index; let valueIndex = $index) {
                  <app-checkbox-menu-element
                    [column]="column()"
                    [data]="data"
                    (valueChange)="
                      filterService.setCheckboxValue(column(), filterIndex, valueIndex, $event)
                    "
                  />
                }
              }
              @case ('radio') {
                @for (data of filter.data; track $index; let valueIndex = $index) {
                  <app-radio-menu-element
                    [column]="column()"
                    [data]="data"
                    (valueChange)="
                      filterService.setRadioValue(column(), filterIndex, valueIndex, $event)
                    "
                  />
                }
              }
              @case ('range') {
                <app-range-menu-element
                  [unit]="filter.unit"
                  [min]="filter.min"
                  [max]="filter.max"
                  (minChange)="filterService.setRangeValue(column(), filterIndex, 'min', $event)"
                  (maxChange)="filterService.setRangeValue(column(), filterIndex, 'max', $event)"
                />
              }
              @case ('search') {
                <app-search-menu-element
                  [column]="column()"
                  [value]="filter.value"
                  (valueChange)="filterService.setValue(column(), filterIndex, $event)"
                />
              }
            }
          }
        </section>
      }
    }
    <ul
      app-filter-menu-element
      [elements]="hideElements()"
      (onElementClick)="tableColumnsService.hideColumn(column())"
    ></ul>
  `,
  styleUrl: './filter-menu.scss',
})
export class FilterMenu {
  readonly filterService: FilterService = inject(FilterService);
  readonly tableColumnsService: TableColumnsService = inject(TableColumnsService);

  column: InputSignal<string> = input.required<string>();
  config: Signal<FilterMenuConfig> = computed(() => this.filterService.filter()[this.column()]);
  filters: Signal<FilterType[] | undefined> = computed(
    () => this.filterService.filter()[this.column()]?.filters ?? undefined,
  );
  hasActiveMenuState: Signal<boolean> = computed(
    () =>
      this.filterService.hasActiveFilter(this.column()) ||
      this.filterService.getActiveSortDirection(this.column()) !== undefined,
  );

  hideElements: Signal<FilterMenuElementItem[]> = computed(() => [
    {
      id: 'hide',
      icon: 'eye_off',
      name: 'table.filter_menu.hide_column',
      disabled: !this.tableColumnsService.canHideColumn(this.column()),
    },
  ]);
}
